import { useTranslation } from '@/features/i18n';
import { useResponsiveCharLimit } from '@/hooks/useResponsiveCharLimit';
import { Button } from '@/ui/buttons/Button';
import { Product } from '@vimond/services/rest-api/types';
import { useCallback, useState } from 'react';

type ProductCardProps = {
  onSelectProduct: (id: number) => void;
  product: Product;
};

const MOBILE_CHAR_LIMIT = 250;
const DESKTOP_CHAR_LIMIT = 300;

const ProductCard = ({ onSelectProduct, product }: ProductCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const charLimit = useResponsiveCharLimit({
    mobile: MOBILE_CHAR_LIMIT,
    desktop: DESKTOP_CHAR_LIMIT,
  });

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const shouldTruncate =
    product.description && product.description.length > charLimit;
  const displayText = isExpanded
    ? product.description
    : product.description?.substring(0, charLimit);

  return (
    <div className="bg-background-primary p-8 rounded-lg shadow-md flex flex-col gap-4 w-[400px] box-border self-start">
      <h2 className="text-text-primary text-xl sm:text-4xl font-bold mb-2 break-words">
        {product.discountedInitPrice !== undefined ? (
          <>
            <span>
              {product.currency} {product.discountedInitPrice}
            </span>
            <span className="line-through text-gray-500 ml-2">
              {product.currency} {product.price}
            </span>
          </>
        ) : (
          <span>
            {product.currency} {product.price}
          </span>
        )}
      </h2>
      <h1 className="text-text-primary text-sm sm:text-xl">
        {product.paymentPlan.name}
      </h1>
      {product.description && (
        <div className="flex flex-col gap-2 flex-1">
          <div className="text-text-primary text-lg break-words">
            {displayText}
            {shouldTruncate && !isExpanded && (
              <button
                onClick={toggleExpanded}
                className="text-blue-500 hover:text-blue-600 font-semibold text-sm ml-1 transition-colors duration-200 align-baseline"
                aria-expanded={isExpanded}
                aria-label="Read more description"
              >
                {`...${t('products.read-more')}`}
              </button>
            )}
            {shouldTruncate && isExpanded && (
              <button
                onClick={toggleExpanded}
                className="text-blue-500 hover:text-blue-600 font-semibold text-sm ml-1 transition-colors duration-200 align-baseline"
                aria-expanded={isExpanded}
                aria-label="Read less description"
              >
                {t('products.read-less')}
              </button>
            )}
          </div>
        </div>
      )}
      <Button
        variant="primary"
        onClick={() => onSelectProduct(product.id)}
        className="align-self-center mt-12 w-28 sm:w-full"
      >
        {t('products.select')}
      </Button>
    </div>
  );
};

export default ProductCard;
